import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { inputStyle } from '../utils/constants';
import { FiUsers, FiPlus, FiEdit2, FiTrash2, FiX, FiAlertCircle, FiSearch, FiMail, FiUser, FiLock, FiShield, FiHash, FiCheckCircle, FiBarChart2, FiCalendar } from 'react-icons/fi';

const toTitleCase = (str) => str ? str.replace(/\S+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()) : str;

export function AddUserModal({ nextEmployeeId, onClose, onBack, onSave, title, infoMessage }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', confirmEmail: '', role: 'user', dob: '', capacity: '', hourlyRate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (form.email !== form.confirmEmail) { setError('Emails do not match'); return; }
    if (form.dob) {
      const dob = new Date(form.dob);
      const today = new Date();
      const age = (today - dob) / (365.25 * 24 * 60 * 60 * 1000);
      if (age < 14) { setError('User must be at least 14 years old'); return; }
      if (age > 120) { setError('Date of birth cannot be more than 120 years ago'); return; }
    }
    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
    setConfirmDialog({
      title: 'Create Account',
      rows: [
        { label: 'Name', value: fullName },
        { label: 'Email', value: form.email },
        ...(form.dob ? [{ label: 'Date of Birth', value: form.dob }] : []),
        { label: 'Capacity', value: form.capacity ? `${form.capacity} hours / day` : 'Not in capacity' },
        ...(form.hourlyRate ? [{ label: 'Hourly Rate', value: `$${form.hourlyRate}/hr` }] : []),
      ],
      subtitle: 'An activation email will be sent to this address.',
      onConfirm: async () => {
        setSaving(true); setConfirmDialog(null);
        try { await onSave({ name: fullName, email: form.email, role: 'user', dob: form.dob || null, capacity: form.capacity ? parseInt(form.capacity) : null, hourly_rate: form.hourlyRate || null }); onClose(); } catch (err) { setError(err.message); }
        setSaving(false);
      },
    });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 460, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onBack && <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, fontSize: 18 }}>&larr;</button>}
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{title || 'Create Account'}</h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><FiX size={20} /></button>
        </div>
        {error && <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}><FiAlertCircle size={14} /> {error}</div>}
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {nextEmployeeId && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Employee ID</label>
              <div style={{ position: 'relative' }}>
                <FiHash size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input value={nextEmployeeId} disabled style={{ ...inputStyle, paddingLeft: 36, opacity: 0.7, cursor: 'not-allowed' }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Auto-generated on creation</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>First Name *</label>
              <div style={{ position: 'relative' }}>
                <FiUser size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input value={form.firstName} onChange={e => set('firstName', toTitleCase(e.target.value))} required placeholder="John" style={{ ...inputStyle, paddingLeft: 36 }} />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Last Name *</label>
              <input value={form.lastName} onChange={e => set('lastName', toTitleCase(e.target.value))} required placeholder="Doe" style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Date of Birth</label>
            <input type="date" value={form.dob} onChange={e => set('dob', e.target.value)}
              max={new Date(new Date().setFullYear(new Date().getFullYear() - 14)).toISOString().split('T')[0]}
              min={new Date(new Date().setFullYear(new Date().getFullYear() - 120)).toISOString().split('T')[0]}
              style={{ ...inputStyle, minHeight: 44, WebkitAppearance: 'none', appearance: 'none' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Email *</label>
            <div style={{ position: 'relative' }}>
              <FiMail size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input type="email" value={form.email} onChange={e => set('email', e.target.value.toLowerCase())} required placeholder="user@example.com" style={{ ...inputStyle, paddingLeft: 36 }} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Confirm Email *</label>
            <div style={{ position: 'relative' }}>
              <FiMail size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input type="email" value={form.confirmEmail} onChange={e => set('confirmEmail', e.target.value.toLowerCase())} required placeholder="Confirm email" style={{ ...inputStyle, paddingLeft: 36, borderColor: form.confirmEmail && form.confirmEmail !== form.email ? '#dc2626' : undefined }} />
            </div>
            {form.confirmEmail && form.confirmEmail !== form.email && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>Emails do not match</div>}
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Work Day Capacity</label>
            <select value={form.capacity} onChange={e => set('capacity', e.target.value)} style={inputStyle}>
              <option value="">Not in capacity</option>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => <option key={h} value={h}>{h} hour{h !== 1 ? 's' : ''} / day</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Hourly Rate ($)</label>
            <input type="number" step="0.01" min="0" value={form.hourlyRate} onChange={e => set('hourlyRate', e.target.value)} placeholder="0.00" style={inputStyle} />
          </div>
          <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--accent-light)', border: '1px solid var(--accent-glow)', color: 'var(--accent)', fontSize: 12 }}>
            {infoMessage || 'An activation email will be sent to this address. The user will set their own password when activating their account. The link expires in 3 days.'}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <motion.button type="button" onClick={onClose} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'var(--bg-card)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Cancel
            </motion.button>
            <motion.button type="submit" disabled={saving || !form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.confirmEmail.trim() || form.email !== form.confirmEmail}
              whileHover={(form.firstName.trim() && form.lastName.trim() && form.email.trim() && form.email === form.confirmEmail) ? { scale: 1.02 } : {}}
              whileTap={(form.firstName.trim() && form.lastName.trim() && form.email.trim() && form.email === form.confirmEmail) ? { scale: 0.98 } : {}}
              style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600,
                cursor: (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || form.email !== form.confirmEmail || saving) ? 'not-allowed' : 'pointer',
                background: (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || form.email !== form.confirmEmail) ? '#94a3b8' : 'var(--accent)',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || form.email !== form.confirmEmail || saving) ? 0.6 : 1,
                boxShadow: (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || form.email !== form.confirmEmail) ? 'none' : '0 4px 12px rgba(37,99,235,0.3)' }}>
              {saving ? 'Creating...' : 'Create Account'}
            </motion.button>
          </div>
        </form>
        <AnimatePresence>
          {confirmDialog && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setConfirmDialog(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 200 }}>
              <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
                onClick={e => e.stopPropagation()}
                style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
                <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{confirmDialog.title}</h3>
                  <button onClick={() => setConfirmDialog(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                </div>
                <div style={{ padding: '18px 22px' }}>
                  {confirmDialog.subtitle && (
                    <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: 'var(--accent-light)', border: '1px solid var(--accent-glow)', color: 'var(--accent)', fontSize: 12, fontWeight: 500 }}>
                      {confirmDialog.subtitle}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {confirmDialog.rows.map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>{row.label}</span>
                        <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                    <button onClick={() => setConfirmDialog(null)}
                      style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                      Cancel
                    </button>
                    <motion.button whileTap={{ scale: 0.97 }} onClick={confirmDialog.onConfirm}
                      style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      Create Account
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

function ManageAccountModal({ user, onClose, onSave, authFetch, showToast, currentUserId }) {
  const nameParts = (user.name || '').trim().split(/\s+/);
  const isSelf = user.id === currentUserId;
  const isAdUser = user.auth_type === 'ad';
  const [form, setForm] = useState({ firstName: nameParts[0] || '', lastName: nameParts.slice(1).join(' ') || '', email: user.email || '', confirmEmail: '', role: user.role || 'user', dob: user.dob ? user.dob.substring(0, 10) : '', passwordResetRequired: !!user.password_reset_required, disabled: !!user.disabled, hourlyRate: user.hourly_rate != null ? user.hourly_rate : 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const [workHours, setWorkHours] = useState(days.map(d => user[`working_hours_${d}`] ?? 0));
  const [hoursSaving, setHoursSaving] = useState(false);
  const totalWeekly = workHours.reduce((s, h) => s + (parseFloat(h) || 0), 0);

  // KPT permissions
  const [allKpts, setAllKpts] = useState([]);
  const [userKptIds, setUserKptIds] = useState(new Set((user.kpts || []).map(k => k.id)));
  const [kptToggling, setKptToggling] = useState(null);
  useEffect(() => {
    authFetch('/api/admin/kpts').then(r => r.ok ? r.json() : []).then(d => setAllKpts(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  const toggleKpt = async (kptId) => {
    setKptToggling(kptId);
    const has = userKptIds.has(kptId);
    const method = has ? 'DELETE' : 'POST';
    const url = has ? `/api/admin/kpts/${kptId}/users/${user.id}` : `/api/admin/kpts/${kptId}/users`;
    const opts = has ? { method } : { method, body: JSON.stringify({ user_id: user.id }) };
    const res = await authFetch(url, opts);
    if (res.ok) {
      setUserKptIds(prev => { const next = new Set(prev); if (has) next.delete(kptId); else next.add(kptId); return next; });
    }
    setKptToggling(null);
  };

  // Roster pattern
  const ROSTER_OPTIONS = [
    { value: 'standard', label: 'Standard (Always On)' },
    { value: '1on1off', label: '1 Week On / 1 Week Off', cycle: 14, on: 7 },
    { value: '2on2off', label: '2 Weeks On / 2 Weeks Off', cycle: 28, on: 14 },
    { value: '2on1off', label: '2 Weeks On / 1 Week Off', cycle: 21, on: 14 },
    { value: '1on2off', label: '1 Week On / 2 Weeks Off', cycle: 21, on: 7 },
    { value: '4on4off', label: '4 Days On / 4 Days Off', cycle: 8, on: 4 },
  ];
  const [roster, setRoster] = useState({ pattern: 'standard', start_date: new Date().toISOString().split('T')[0] });
  const [rosterSaving, setRosterSaving] = useState(false);
  const [rosterLoaded, setRosterLoaded] = useState(false);
  useEffect(() => {
    authFetch(`/api/users/${user.id}/roster`).then(r => r.ok ? r.json() : null).then(d => {
      if (d && d.pattern) setRoster({ pattern: d.pattern, start_date: d.start_date ? d.start_date.substring(0, 10) : new Date().toISOString().split('T')[0] });
      setRosterLoaded(true);
    }).catch(() => setRosterLoaded(true));
  }, []);
  const saveRoster = async () => {
    setRosterSaving(true);
    const opt = ROSTER_OPTIONS.find(o => o.value === roster.pattern);
    const body = { pattern: roster.pattern, start_date: roster.start_date, cycle_days: opt?.cycle || 14, days_on: opt?.on || 7 };
    await authFetch(`/api/users/${user.id}/roster`, { method: 'PUT', body: JSON.stringify(body) });
    if (showToast) showToast('Work roster saved');
    setRosterSaving(false);
  };

  // Capacity view
  const [capacityDays, setCapacityDays] = useState(null);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [showCapacity, setShowCapacity] = useState(false);
  const [rescheduleOp, setRescheduleOp] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  const loadCapacity = async () => {
    setCapacityLoading(true);
    try {
      const res = await authFetch(`/api/issues/user-capacity-daily?user_id=${user.id}&weeks=4`);
      if (res.ok) setCapacityDays(await res.json());
    } catch {}
    setCapacityLoading(false);
  };

  const rescheduleOperation = async () => {
    if (!rescheduleOp || !rescheduleDate) return;
    setRescheduling(true);
    try {
      const res = await authFetch(`/api/issues/operations/${rescheduleOp.id}/reschedule`, { method: 'PATCH', body: JSON.stringify({ planned_date: rescheduleDate }) });
      if (res.ok) {
        if (showToast) showToast('Operation rescheduled');
        setRescheduleOp(null);
        loadCapacity();
      }
    } catch {}
    setRescheduling(false);
  };

  const saveWorkHours = async () => {
    setHoursSaving(true);
    const body = {};
    days.forEach((d, i) => body[d] = parseFloat(workHours[i]) || 0);
    await authFetch(`/api/users/${user.id}/working-hours`, { method: 'PATCH', body: JSON.stringify(body) });
    if (showToast) showToast('Working hours saved');
    setHoursSaving(false);
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const emailChanged = form.email.toLowerCase() !== user.email.toLowerCase();

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    if (emailChanged && form.email !== form.confirmEmail) {
      setError('Email addresses do not match');
      setSaving(false);
      return;
    }
    try { await onSave({ name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(), email: form.email, role: form.role, dob: form.dob || null, disabled: form.disabled, password_reset_required: form.passwordResetRequired, hourly_rate: form.hourlyRate !== '' ? parseFloat(form.hourlyRate) : null }); onClose(); } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const avatarUrl = user.avatar ? `/api/users/avatar/${user.avatar}` : null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
        style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 520, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Fixed Header */}
        <div style={{ padding: '20px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Manage Account</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><FiX size={20} /></button>
          </div>

          {error && <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 12, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}><FiAlertCircle size={14} /> {error}</div>}

          {/* User info header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--bg-input)', borderRadius: 10, marginBottom: 16, border: '1px solid var(--border)' }}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FiUser size={18} color="var(--accent)" />
              </div>
            )}
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{user.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>ID: {user.employee_id || '-'}</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {isAdUser ? <span style={{ padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}>ACTIVE DIRECTORY</span> : null}
              {user.disabled ? <span style={{ padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>DISABLED</span> : null}
              {!user.email_verified ? <span style={{ padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}>NOT VERIFIED</span> : null}
            </div>
          </div>
        </div>

        {/* Scrollable Body */}
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
        <div style={{ overflowY: 'auto', padding: '0 28px', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {isAdUser && (
            <div style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #bae6fd', background: '#f0f9ff', fontSize: 12, color: '#0369a1' }}>
              Name, email and password are managed by Active Directory and cannot be changed here.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>First Name *</label>
              <div style={{ position: 'relative' }}>
                <FiUser size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input value={form.firstName} onChange={e => set('firstName', toTitleCase(e.target.value))} required placeholder="John" disabled={isSelf || isAdUser} style={{ ...inputStyle, paddingLeft: 36, ...((isSelf || isAdUser) ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }} />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Last Name *</label>
              <input value={form.lastName} onChange={e => set('lastName', toTitleCase(e.target.value))} required placeholder="Doe" disabled={isSelf || isAdUser} style={{ ...inputStyle, ...((isSelf || isAdUser) ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Date of Birth</label>
            <input type="date" value={form.dob} onChange={e => set('dob', e.target.value)}
              max={new Date(new Date().setFullYear(new Date().getFullYear() - 14)).toISOString().split('T')[0]}
              min={new Date(new Date().setFullYear(new Date().getFullYear() - 120)).toISOString().split('T')[0]}
              style={{ ...inputStyle, minHeight: 44, WebkitAppearance: 'none', appearance: 'none' }} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Email Address</label>
            <div style={{ position: 'relative' }}>
              <FiMail size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input type="email" value={form.email} onChange={e => set('email', e.target.value.toLowerCase())} required placeholder="user@example.com" disabled={isSelf || isAdUser} style={{ ...inputStyle, paddingLeft: 36, ...((isSelf || isAdUser) ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }} />
            </div>
          </div>

          {emailChanged && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#d97706', marginBottom: 4, display: 'block' }}>Confirm New Email *</label>
              <div style={{ position: 'relative' }}>
                <FiMail size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#d97706' }} />
                <input type="email" value={form.confirmEmail} onChange={e => set('confirmEmail', e.target.value.toLowerCase())} required placeholder="Re-enter new email" style={{ ...inputStyle, paddingLeft: 36, borderColor: 'rgba(217,119,6,0.3)' }} />
              </div>
            </div>
          )}

          {/* Working hours are edited by team supervisors on the My Team
              page — removed from this admin modal to keep the two roles
              from competing over the same setting. */}

          {/* Work Roster */}
          {rosterLoaded && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Work Roster</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3, display: 'block' }}>Pattern</label>
                  <select value={roster.pattern} onChange={e => setRoster(r => ({ ...r, pattern: e.target.value }))}
                    style={{ ...inputStyle, minHeight: 38 }}>
                    {ROSTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {roster.pattern !== 'standard' && (
                  <div style={{ flex: '1 1 140px' }}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3, display: 'block' }}>Cycle Start Date</label>
                    <input type="date" value={roster.start_date} onChange={e => setRoster(r => ({ ...r, start_date: e.target.value }))}
                      style={{ ...inputStyle, minHeight: 38 }} />
                  </div>
                )}
              </div>
              {roster.pattern !== 'standard' && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Set the start date to a day when this user begins their ON period.
                </div>
              )}
              <button type="button" onClick={saveRoster} disabled={rosterSaving}
                style={{ marginTop: 8, padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {rosterSaving ? 'Saving...' : 'Save Roster'}
              </button>
            </div>
          )}

          {/* Hourly Rate */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Hourly Rate ($)</label>
            <input type="number" step="0.01" min="0" value={form.hourlyRate} onChange={e => set('hourlyRate', e.target.value)} placeholder="0.00"
              style={{ ...inputStyle, maxWidth: 160 }} />
          </div>

          {/* Team (read-only) */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Team</label>
            <input value={user.team_name || 'No team assigned'} disabled
              style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} />
          </div>

          {/* KPT Permissions (read-only display + toggle) */}
          {allKpts.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <FiShield size={13} color="var(--text-secondary)" />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>KPT Permissions</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {allKpts.map(k => {
                  const active = userKptIds.has(k.id);
                  const toggling = kptToggling === k.id;
                  return (
                    <button key={k.id} type="button" onClick={() => toggleKpt(k.id)} disabled={toggling}
                      style={{
                        padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: toggling ? 'wait' : 'pointer',
                        border: `1.5px solid ${active ? k.color : 'var(--border)'}`,
                        background: active ? `${k.color}15` : 'var(--bg-input)',
                        color: active ? k.color : 'var(--text-muted)',
                        opacity: toggling ? 0.6 : 1, transition: 'all 0.15s'
                      }}>
                      {k.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Account Actions - hidden for AD users */}
          {!isAdUser && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>Account Actions</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: isSelf ? 'not-allowed' : 'pointer', opacity: isSelf ? 0.5 : 1 }}>
                <input type="checkbox" checked={form.passwordResetRequired} disabled={isSelf}
                  onChange={e => set('passwordResetRequired', e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: '#d97706', cursor: isSelf ? 'not-allowed' : 'pointer' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#d97706' }}>Force Password Reset</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>User must change password on next login</div>
                </div>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: `1px solid ${form.disabled ? 'rgba(220,38,38,0.3)' : 'var(--border)'}`, background: form.disabled ? 'rgba(220,38,38,0.04)' : 'var(--bg-input)', cursor: isSelf ? 'not-allowed' : 'pointer', opacity: isSelf ? 0.5 : 1 }}>
                <input type="checkbox" checked={form.disabled} disabled={isSelf}
                  onChange={e => set('disabled', e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: '#dc2626', cursor: isSelf ? 'not-allowed' : 'pointer' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>Disable Account</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Prevent user from logging in</div>
                </div>
              </label>
            </div>
          )}

        </div>
        {/* Fixed Footer */}
        <div style={{ padding: '16px 28px 20px', flexShrink: 0, borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
            <motion.button type="button" onClick={onClose} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'var(--bg-card)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Cancel
            </motion.button>
            <motion.button type="submit" disabled={saving} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: saving ? 0.7 : 1, boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </motion.button>
        </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? ((parts[0][0] + parts[parts.length - 1][0])).toUpperCase() : parts[0][0].toUpperCase();
}

export default function ManageUsers({ embedded }) {
  const { authFetch, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [manageUser, setManageUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [nextEmployeeId, setNextEmployeeId] = useState('');
  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = () => {
    authFetch('/api/users').then(r => r.ok ? r.json() : []).then(data => { setUsers(Array.isArray(data) ? data : []); setLoading(false); }).catch(() => { setUsers([]); setLoading(false); });
  };

  const fetchNextEmployeeId = () => {
    authFetch('/api/users/next-employee-id').then(r => r.ok ? r.json() : {}).then(d => setNextEmployeeId(d.employee_id || ''));
  };

  const openAddUser = () => { fetchNextEmployeeId(); setShowAddModal(true); };

  const createUser = async (form) => {
    const res = await authFetch('/api/users', { method: 'POST', body: JSON.stringify(form) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Server error');
    loadUsers();
    if (data.email_sent === false) {
      showToast(`Account created for "${form.name}". Activation email could not be sent — please resend manually.`);
    } else {
      showToast(`Account created for "${form.name}". Activation email sent.`);
    }
  };

  const updateUser = async (form) => {
    const res = await authFetch(`/api/users/${manageUser.id}`, { method: 'PUT', body: JSON.stringify(form) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    loadUsers();
    showToast(`Account updated for "${form.name}"`);
  };

  const deleteUser = async (id) => {
    const u = users.find(u => u.id === id);
    const res = await authFetch(`/api/users/${id}`, { method: 'DELETE' });
    if (res.ok) {
      setUsers(prev => prev.filter(u => u.id !== id));
      showToast(`User "${u?.name}" deleted`);
    }
    setConfirmDialog(null);
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.employee_id?.includes(q);
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (loading) return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="skeleton" style={{ width: 180, height: 28, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: 160, height: 14 }} />
      </div>
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 16, marginBottom: 8, border: '1px solid var(--border)' }}>
          <div className="skeleton" style={{ width: '60%', height: 16, marginBottom: 8 }} />
          <div className="skeleton" style={{ width: '40%', height: 12 }} />
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            style={{ position: 'fixed', top: 20, right: 20, zIndex: 999, padding: '12px 20px', borderRadius: 10,
              background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a',
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <FiCheckCircle size={16} /> {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddModal && <AddUserModal nextEmployeeId={nextEmployeeId} onClose={() => setShowAddModal(false)} onSave={createUser} />}
        {manageUser && <ManageAccountModal user={manageUser} onClose={() => { setManageUser(null); loadUsers(); }} onSave={updateUser} authFetch={authFetch} showToast={showToast} currentUserId={currentUser?.id} />}
      </AnimatePresence>

      {!embedded && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 800, marginBottom: 4, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>Manage Users</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>{users.length} user{users.length !== 1 ? 's' : ''} in the system</p>
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div style={{ flex: '1 1 280px', position: 'relative' }}>
          <FiSearch size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..."
            style={{ width: '100%', padding: '12px 12px 12px 42px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={openAddUser}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
          <FiPlus size={16} /> Create Account
        </motion.button>
      </motion.div>

      {/* User list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map((u, idx) => (
          <motion.div key={u.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
            style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
              background: u.disabled ? 'rgba(220,38,38,0.02)' : 'var(--bg-card)',
              borderRadius: 10, border: `1px solid ${u.disabled ? 'rgba(220,38,38,0.15)' : 'var(--border)'}`,
              flexWrap: 'wrap', opacity: u.disabled ? 0.7 : 1
            }}>
            {u.avatar ? (
              <img src={`/api/users/avatar/${u.avatar}`} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), #0ea5e9)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{getInitials(u.name)}</span>
              </div>
            )}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</span>
                {(u.kpts || []).map(k => (
                  <span key={k.id} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 600, background: `${k.color || '#6b7280'}18`, color: k.color || '#6b7280' }}>
                    {k.name}
                  </span>
                ))}
                {u.auth_type === 'ad' ? <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}>AD</span> : null}
                {u.activation_token ? <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>PENDING ACTIVATION</span> : null}
                {u.disabled && !u.activation_token ? <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: '#fef2f2', color: '#dc2626' }}>DISABLED</span> : null}
                {!u.email_verified && !u.activation_token ? <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: '#fffbeb', color: '#d97706' }}>NOT VERIFIED</span> : null}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{u.email}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 1 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>ID: {u.employee_id || '-'}</span>
                {u.hourly_rate != null && parseFloat(u.hourly_rate) > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: '#059669' }}>${parseFloat(u.hourly_rate).toFixed(2)}/hr</span>}
              </div>
              {u.team_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: `${u.team_color || '#6b7280'}15`, color: u.team_color || '#6b7280' }}>{u.team_name}</span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <motion.button whileTap={{ scale: 0.9 }} onClick={() => setManageUser(u)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                <FiEdit2 size={12} /> Manage
              </motion.button>
              {u.id !== currentUser?.id && u.auth_type !== 'ad' && (
                <motion.button whileTap={{ scale: 0.9 }} onClick={() => setConfirmDialog({
                  title: 'Delete Account',
                  rows: [
                    { label: 'Name', value: u.name },
                    { label: 'Email', value: u.email },
                    { label: 'Employee ID', value: u.employee_id || '-' },
                  ],
                  confirmLabel: 'Delete Account',
                  confirmColor: '#dc2626',
                  onConfirm: () => deleteUser(u.id)
                })}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.04)', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                  <FiTrash2 size={12} /> Delete
                </motion.button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
          <FiUsers size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontSize: 14 }}>No users found</p>
        </div>
      )}

      {/* Themed Confirmation Dialog */}
      <AnimatePresence>
        {confirmDialog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 300 }}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
              <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{confirmDialog.title}</h3>
                <button onClick={() => setConfirmDialog(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '18px 22px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {confirmDialog.rows.map((row, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>{row.label}</span>
                      <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{row.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                  <button onClick={() => setConfirmDialog(null)}
                    style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                    Cancel
                  </button>
                  <motion.button whileTap={{ scale: 0.97 }} onClick={confirmDialog.onConfirm}
                    style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: confirmDialog.confirmColor || 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {confirmDialog.confirmLabel || 'Confirm'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
